import { forwardRef, type ReactNode } from 'react';

import { cva } from 'cva';
import { Input as RACInput } from 'react-aria-components/Input';
import {
  TextField as RACTextField,
  type TextFieldProps as RACTextFieldProps,
  type ValidationResult,
} from 'react-aria-components/TextField';

import { getPasswordManagerProps } from '../../utils/get_password_manager_props';
import { FieldDescription } from '../form/field_description';
import { FieldError } from '../form/field_error';
import { FieldLabel } from '../form/field_label';
import type { IconName } from '../icon/generated_icons';
import { Icon } from '../icon/icon';

import styles from './text_field.module.css';

export const textFieldVariants = cva({
  base: styles.text_field,
  variants: {
    size: {
      s: styles.size_s,
      m: styles.size_m,
      l: styles.size_l,
    },
    readOnly: {
      true: styles.read_only,
    },
  },
  defaultVariants: {
    size: 'm',
  },
});

export type TextFieldProps = RACTextFieldProps & {
  description?: ReactNode;
  errorMessage?: string | ((validation: ValidationResult) => string);
  icon?: IconName;
  inputRef?: React.Ref<HTMLInputElement>;
  isDisabled?: boolean;
  isInvalid?: boolean;
  isRequired?: boolean;
  label?: string;
  maxLength?: number;
  placeholder?: string;
  size?: 's' | 'm' | 'l';
  tooltip?: React.ReactNode;
};

/**
 * Text field component that can be used to input text.
 *
 * See `TextAreaField` and `NumberField` components.
 *
 * See https://react-spectrum.adobe.com/react-aria/TextField.html for the underlying
 * implementation.
 *
 * ```tsx
 * <TextField
 *   label="Label"
 *   isRequired
 *   placeholder="Placeholder"
 * />
 * ```
 */
export const TextField = forwardRef<HTMLDivElement, TextFieldProps>(
  (
    {
      autoComplete,
      className,
      inputRef,
      description,
      errorMessage,
      icon,
      isReadOnly,
      isRequired,
      label,
      maxLength,
      size = 'm',
      tooltip,
      ...rest
    },
    ref,
  ) => {
    const classes = textFieldVariants({
      size,
      readOnly: isReadOnly,
      className,
    });

    return (
      <RACTextField
        ref={ref}
        autoComplete={autoComplete}
        isRequired={isRequired}
        maxLength={maxLength}
        className={classes}
        isReadOnly={isReadOnly}
        {...rest}
      >
        {label && (
          <FieldLabel isRequired={isRequired} tooltip={tooltip}>
            {label}
          </FieldLabel>
        )}
        <div>
          {icon && <Icon name={icon} className={styles.icon} size={size} />}
          <RACInput ref={inputRef} {...getPasswordManagerProps(autoComplete)} />
        </div>
        {description && <FieldDescription>{description}</FieldDescription>}
        <FieldError>{errorMessage}</FieldError>
      </RACTextField>
    );
  },
);
